import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { fmtCents } from '@/components/money/format.js';
import TrustCardWords, { NoClientMoney } from '@/components/money/TrustCardWords.jsx';
import BankExports from '@/components/money/BankExports.jsx';

// THE TRUST NUMBER (TRACON-MONEY-SPEC §4.4, §9.2): one card per trust account.
// Cash is what the bank said, with its as-of; each term below it is a sum of
// rows you can open; the last line is NDEMNIFLY's. Anything built on an
// unknown is PROVISIONAL, in red, with the rows that make it so. The screen
// computes nothing the server did not; it renders the breakdown it was given.
//
// Every row is labelled the way the broker would say it (R22): counterparty,
// insured or deal, date, amount, source. The UUID is on hover, never the label.
//
// A row whose trust account nobody has stated ("account assumed", or a
// collected transaction on no card at all) carries the door (R18): choose the
// account, confirm. The server records who and when; nothing here guesses.
//
// Bank exports enter here (§9.1, R20): a CSV from the bank goes straight to
// the bank-line adapter and the answer is the count and the through-date, or
// exactly why the file could not be read.
//
// An inter-account row (money this account paid out for a client another
// account collected) settles by a human's confirmation (R19): the server
// proposes the bank line that matches it, or the broker names the line
// (money in on this account, or the mirror going out of the paying one).
// Never by amount alone, never silently.

const TERM_LABEL = {
  carrier_payable: 'Owed to carriers (collected, not yet remitted)',
  client_payable: 'Owed to clients (refunds due)',
  held_credits: 'Held credits',
  interaccount_payable: 'Owed to other trust accounts (they paid carriers for money collected here)',
  owed_to_operating: 'Owed to Operating (fronts paid from outside trust)',
  fronts_receivable: 'Fronted for clients (receivable)',
  carrier_credits: 'Carrier credits owed to us',
  interaccount_receivable: 'Owed by other trust accounts',
  commission_clawback: 'Commission owed back from Income (returned premium)',
};
const MINUS = new Set(['carrier_payable', 'client_payable', 'held_credits', 'interaccount_payable', 'owed_to_operating']);
// R19: the three inter-account terms are one fact seen from two accounts; the settlement control sits on all of them.
const SETTLE_TERMS = new Set(['interaccount_receivable', 'interaccount_payable', 'owed_to_operating']);
// Which door a term's rows post through (R18). Rows of other terms have no door.
const STATE_KIND = { carrier_payable: 'transaction', client_payable: 'refund', commission_clawback: 'refund', carrier_credits: 'credit_line' };
const selectCls = 'mt-1 h-11 w-full rounded-card border border-border bg-bg px-2 text-sm text-text';
const smallSelectCls = 'h-9 rounded-card border border-border bg-bg px-2 text-xs text-text';

function rowLabel(r) {
  return [r.counterparty, r.subject].filter(Boolean).join(' · ') || r.crm_deal_id || 'unnamed';
}

function RowIdentity({ r }) {
  return (
    <span className="min-w-0" title={`${r.id}${r.crm_deal_id ? ` · deal ${r.crm_deal_id}` : ''}${r.policy_transaction_id ? ` · txn ${r.policy_transaction_id}` : ''}`}>
      <span className="text-text">{rowLabel(r)}</span>
      {r.date ? <span className="text-text-dim"> · {r.date}</span> : null}
      {r.source ? <span className="text-text-dim"> · {r.source}</span> : null}
      {r.collected_by && r.kind === 'interaccount' ? <span className="text-text-dim"> · collected by {r.collected_by}</span> : null}
      {r.paid_from && r.kind !== 'interaccount' ? <span className="text-text-dim"> · paid by {r.paid_from}</span> : null}
    </span>
  );
}

// R18: the door on the row. Posts { kind, id, trust_account_id } once, then the page re-reads.
function StateAccount({ kind, row, accounts, onStated }) {
  const [accountId, setAccountId] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!accountId) { toast.error('Choose the trust account first.'); return; }
    setBusy(true);
    try {
      const r = await api.post('/api/money/state-account', { kind, id: row.id, trust_account_id: accountId });
      toast.success(`${rowLabel(row)}: account stated as ${r.account?.name || 'the chosen account'}.`);
      await onStated();
    } catch (e) {
      toast.error(e.body?.code || e.body?.error || e.message || 'Could not state the account');
    } finally { setBusy(false); }
  };
  return (
    <span className="flex shrink-0 items-center gap-1">
      <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={smallSelectCls} aria-label="Trust account">
        <option value="">state the account</option>
        {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <Button size="sm" variant="outline" onClick={submit} disabled={busy || !accountId}>{busy ? 'Saving...' : 'Confirm'}</Button>
    </span>
  );
}

// R19: the settlement control on an inter-account row. With a proposal, one
// confirm settles every row the proposal covers; without one, the broker
// names the bank line from either account's lines.
function SettleInterAccount({ row, onSettled }) {
  const [lineId, setLineId] = useState('');
  const [lines, setLines] = useState(null);
  const [busy, setBusy] = useState(false);
  const p = row.proposal || null;
  const post = async (allocationIds, bankLineId) => {
    setBusy(true);
    try {
      const r = await api.post('/api/money/settle-interaccount', { allocation_ids: allocationIds, bank_line_id: bankLineId });
      toast.success(`${allocationIds.length} row${allocationIds.length === 1 ? '' : 's'} settled against the ${r.bank_line?.posted_on || ''} bank line${r.remaining ? `, ${fmtCents(r.remaining)} of the line still unmatched` : ''}.`);
      await onSettled();
    } catch (e) {
      toast.error(e.body?.code || e.body?.error || e.message || 'Could not settle');
    } finally { setBusy(false); }
  };
  const loadLines = async () => {
    if (lines) return;
    try {
      const [inRes, outRes] = await Promise.all([
        row.from_account_id ? api.get(`/api/money/bank-lines/${row.from_account_id}?limit=200`) : { rows: [] },
        row.collected_by_account_id ? api.get(`/api/money/bank-lines/${row.collected_by_account_id}?limit=200`) : { rows: [] },
      ]);
      const inc = (inRes.rows || []).filter((l) => Number(l.amount) > 0).map((l) => ({ ...l, side: 'in' }));
      const out = (outRes.rows || []).filter((l) => Number(l.amount) < 0).map((l) => ({ ...l, side: 'out' }));
      setLines([...inc, ...out].sort((a, b) => String(b.posted_on).localeCompare(String(a.posted_on))));
    } catch (e) { toast.error(e.message || 'Could not load the bank lines'); setLines([]); }
  };
  if (p) {
    return (
      <span className="flex min-w-0 flex-wrap items-center gap-1 text-xs">
        <span className="min-w-0 text-text-muted" title={p.bank_line_id}>
          arrived {p.posted_on} as {fmtCents(p.amount)}{p.basis === 'group' ? ` (one line, ${p.allocation_ids.length} rows)` : ''}
          {p.paired_line ? `, left ${p.paying_account_name || 'the paying account'} ${p.paired_line.posted_on}` : ''}
        </span>
        <Button size="sm" variant="outline" onClick={() => post(p.allocation_ids, p.bank_line_id)} disabled={busy}>{busy ? 'Saving...' : 'Confirm settled'}</Button>
      </span>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-1">
      <select value={lineId} onFocus={loadLines} onChange={(e) => setLineId(e.target.value)} className={smallSelectCls} aria-label="Bank line that settled this">
        <option value="">{lines === null ? 'name the bank line' : lines.length ? 'name the bank line' : 'no bank lines loaded'}</option>
        {(lines || []).map((l) => <option key={l.id} value={l.id}>{l.posted_on} · {l.side === 'in' ? 'in' : 'out'} · {l.payee || l.kind || ''} · {fmtCents(l.amount)}</option>)}
      </select>
      <Button size="sm" variant="outline" onClick={() => post([row.id], lineId)} disabled={busy || !lineId}>{busy ? 'Saving...' : 'Settled'}</Button>
    </span>
  );
}

function Term({ k, t, accounts, onStated }) {
  const [open, setOpen] = useState(false);
  const kind = STATE_KIND[k] || null;
  return (
    <li className="py-2">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-baseline justify-between gap-2 text-left">
        <span className="text-sm text-text">{MINUS.has(k) ? 'less' : 'plus'} {TERM_LABEL[k] || k} <span className="text-xs text-text-dim">({t.count} row{t.count === 1 ? '' : 's'})</span></span>
        <span className={`font-mono text-sm ${t.provisional ? 'text-danger' : 'text-text'}`}>{MINUS.has(k) ? '−' : '+'}{fmtCents(t.amount)}{t.provisional ? ' provisional' : ''}</span>
      </button>
      {open ? (
        <ul className="mt-2 border-t border-border text-[12px] text-text-muted">
          {t.rows.length === 0 ? <li className="py-1">no rows</li> : t.rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
              <RowIdentity r={r} />
              <span className="flex items-center gap-2">
                {r.attributed === false && kind && r.statable !== false ? <StateAccount kind={kind} row={r} accounts={accounts} onStated={onStated} /> : null}
                {SETTLE_TERMS.has(k) ? <SettleInterAccount row={r} onSettled={onStated} /> : null}
                {r.attributed === false && !(kind && r.statable !== false) ? <span className="text-xs text-danger">account assumed</span> : null}
                <span className={`font-mono ${r.amount === null ? 'text-danger' : 'text-text'}`}>{r.amount === null ? 'unknown' : fmtCents(r.amount)}</span>
              </span>
            </li>
          ))}
          {t.pending.filter((p) => p.reason).map((p, i) => <li key={`why-${i}`} className="py-1 text-danger">{p.reason}</li>)}
        </ul>
      ) : null}
    </li>
  );
}


// STEP 6 (Night 11): a closing account's residual leaves in ONE transfer to income. This is not a per-deal
// sweep: the door names the bank line that WAS the transfer (loaded from the account's export), and the
// server settles every open deposit of the account by it without deriving a per-deal commission. The
// rates stay the broker's to state when the carrier's statement arrives. A payable still owed to a
// carrier is not touched and stays on the card.
function ClosingTransfer({ a, onDone }) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lineId, setLineId] = useState('');
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setOpen(true);
    try { setPreview(await api.get(`/api/money/closing-transfer/${a.account.id}`)); }
    catch (e) { toast.error(e.message || 'Could not load the closing transfer'); setOpen(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/money/closing-transfer', { account_id: a.account.id, bank_line_id: lineId });
      toast.success(r.resumed
        ? `Completed: ${r.stamped_count} deposit${r.stamped_count === 1 ? '' : 's'} settled by the transfer recorded earlier (${r.previously_stamped} already carried it).`
        : `Recorded: ${fmtCents(r.amount)} left ${a.account.name} on ${r.transferred_on}; ${r.stamped_count} deposit${r.stamped_count === 1 ? '' : 's'} settled by it.`);
      setOpen(false); setArmed(false); setLineId('');
      await onDone();
    } catch (e) {
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not record the transfer');
    } finally { setBusy(false); }
  };

  if (!open) {
    return <div className="mt-3 border-t border-border pt-3"><Button size="sm" variant="outline" onClick={load}>Transfer residual to income</Button></div>;
  }
  const lines = preview?.outgoing_lines || [];
  const rows = preview?.rows || [];
  return (
    <div className="mt-3 border-t border-border pt-3 text-sm">
      <div className="text-text">Transfer residual to income</div>
      {preview === null ? <p className="mt-1 text-xs text-text-muted">Loading&hellip;</p> : (
        <>
          <p className="mt-1 text-xs text-text-muted">
            {rows.length} open deposit{rows.length === 1 ? '' : 's'} in {a.account.name} will be marked settled by this one transfer. No per-deal commission is worked out from it.
          </p>
          <ul className="mt-1 text-[12px] text-text-muted">
            {rows.map((r) => <li key={r.id}>{r.counterparty || 'counterparty not on file'} · received {r.received_on || 'day unknown'} · gross {fmtCents(r.gross)}</li>)}
          </ul>
          <label className="mt-2 block text-xs text-text-muted">The bank line that was the transfer
            <select value={lineId} onChange={(e) => { setLineId(e.target.value); setArmed(false); }} className={selectCls}>
              <option value="">{lines.length ? 'choose the bank line' : 'no outgoing bank lines loaded: load the export through the transfer date'}</option>
              {lines.map((l) => <option key={l.id} value={l.id}>{l.posted_on} · {l.payee || l.kind || ''} · {fmtCents(l.amount)}{l.resume ? ` · recorded, ${l.resume.gap} of ${l.resume.row_count} deposit${l.resume.row_count === 1 ? '' : 's'} still to settle: confirm again` : ''}</option>)}
            </select>
          </label>
          <div className="mt-2 flex gap-2">
            {!armed
              ? <Button size="sm" onClick={() => setArmed(true)} disabled={!lineId}>Review</Button>
              : <Button size="sm" onClick={submit} disabled={busy || !lineId}>{busy ? 'Recording...' : `Confirm: settle ${rows.length} deposit${rows.length === 1 ? '' : 's'}`}</Button>}
            <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setArmed(false); }} disabled={busy}>Cancel</Button>
          </div>
        </>
      )}
    </div>
  );
}

// R24 EXIT: the wholesaler's statement or remittance settles the receivable. The broker says what
// settled it (required: it is the only record of the evidence); the server stamps who and when and
// the row leaves the card on the re-read. Nothing here computes the amount.
function SettleWholesalerReceivable({ row, onSettled }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/money/wholesaler-receivables/${encodeURIComponent(row.id)}/settle`, { note });
      toast.success('Receivable settled; it leaves the card.');
      setOpen(false); setNote('');
      await onSettled();
    } catch (e) {
      toast.error(e.body?.detail || e.body?.code || e.message || 'Could not settle the receivable');
    } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Settled by statement</Button>;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1">
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="which statement or remittance, and its date"
        aria-label="What settled this receivable"
        className="h-9 min-w-[16rem] rounded-card border border-border bg-bg px-2 text-xs text-text"
      />
      <Button size="sm" onClick={submit} disabled={busy || !note.trim()}>{busy ? 'Saving...' : 'Confirm settled'}</Button>
      <Button size="sm" variant="ghost" onClick={() => { setOpen(false); setNote(''); }} disabled={busy}>Cancel</Button>
    </span>
  );
}

// R24: deposits whose full gross went to a wholesaler. Listed with their own label; not part of the
// number above (the agency's share is owed back by the wholesaler, it is not held here).
function WholesalerReceivable({ t, onSettled }) {
  if (!t || t.count === 0) return null;
  return (
    <div className="mt-2 border-t border-border pt-2">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-text">Owed to us by wholesalers (paid in full, not in the number above) <span className="text-xs text-text-dim">({t.count} row{t.count === 1 ? '' : 's'})</span></span>
        <span className={`font-mono text-sm ${t.provisional ? 'text-danger' : 'text-text'}`}>{fmtCents(t.amount)}{t.provisional ? ' provisional' : ''}</span>
      </div>
      <ul className="mt-1 text-[12px] text-text-muted">
        {t.rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-1">
            <RowIdentity r={r} />
            <span className="flex items-center gap-2">
              <SettleWholesalerReceivable row={r} onSettled={onSettled} />
              <span className={`font-mono ${r.amount === null ? 'text-danger' : 'text-text'}`}>{r.amount === null ? 'commission not stated' : fmtCents(r.amount)}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AccountCard({ a, accounts, onStated }) {
  return (
    <div className="rounded-card border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-text">{a.account.name}{a.account.active ? '' : <span className="ml-2 text-xs text-text-dim">closed</span>}</h3>
        <span className="font-mono text-xs text-text-dim">{a.bank_lines.lines} bank line{a.bank_lines.lines === 1 ? '' : 's'}{a.bank_lines.last ? ` through ${a.bank_lines.last}` : ''}</span>
      </div>
      <div className="mt-3 flex items-baseline justify-between">
        <span className="text-sm text-text">Cash, per the bank{a.cash.as_of ? <span className="text-xs text-text-dim"> as of {a.cash.as_of}</span> : null}</span>
        <span className={`font-mono text-sm ${a.cash.provisional ? 'text-danger' : 'text-text'}`}>{a.cash.amount === null ? 'unknown' : fmtCents(a.cash.amount)}</span>
      </div>
      {a.cash.provisional ? <p className="mt-1 text-xs text-danger">{a.cash.reason}. Load a bank export below.</p> : null}
      <TrustCardWords w={a.words} />
      <div className="mt-3 text-xs font-semibold uppercase tracking-wider text-text-dim">The rows behind each term</div>
      <ul className="mt-2 divide-y divide-border border-t border-border">
        {Object.entries(a.terms).map(([k, t]) => <Term key={k} k={k} t={t} accounts={accounts} onStated={onStated} />)}
      </ul>
      <WholesalerReceivable t={a.wholesaler_receivable} onSettled={onStated} />
      <div className="mt-3 flex items-baseline justify-between border-t border-border pt-3">
        <span className="text-sm font-semibold text-text">NDEMNIFLY&apos;s (unswept commission, fees, interest)</span>
        <span className={`font-mono text-base font-semibold ${a.ndemnifly.provisional ? 'text-danger' : 'text-text'}`}>{a.ndemnifly.amount === null ? 'unknown' : fmtCents(a.ndemnifly.amount)}{a.ndemnifly.provisional ? ' provisional' : ''}</span>
      </div>
      {a.ndemnifly.provisional ? <ul className="mt-1 text-xs text-danger">{a.ndemnifly.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul> : null}
      {a.account.closing ? <ClosingTransfer a={a} onDone={onStated} /> : null}
    </div>
  );
}

// The collected transactions on no card: each is a row with the door (R18, R22), never a bare count.
function Unattributed({ rows, accounts, onStated }) {
  if (!rows.length) return null;
  return (
    <div className="mt-4 rounded-card border border-danger/40 bg-surface p-4">
      <h3 className="text-sm font-semibold text-danger">{rows.length} collected transaction{rows.length === 1 ? '' : 's'} name no trust account</h3>
      <p className="mt-1 text-xs text-text-muted">No card above carries them and their owed total is in no number here. State where each client&apos;s money landed.</p>
      <ul className="mt-2 divide-y divide-border text-[12px] text-text-muted">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
            <RowIdentity r={r} />
            <span className="flex items-center gap-2">
              <StateAccount kind="transaction" row={r} accounts={accounts} onStated={onStated} />
              <span className="font-mono text-text">{r.collected === null || r.collected === undefined ? '' : `collected ${fmtCents(r.collected)}`}{r.owed === null ? <span className="text-danger"> · owed unknown</span> : ` · owed ${fmtCents(r.owed)}`}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MoneyTrust() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try { setData(await api.get('/api/money/trust')); setError(''); }
    catch (e) { setError(e.message || 'Could not load the trust number'); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const accounts = data?.accounts || [];
  const trustAccounts = accounts.map((a) => a.account);
  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold text-text">Trust</h2>
      <p className="mt-1 text-sm text-text-muted">What the bank says is there, less what each account still owes, plus what is owed to it. The remainder is NDEMNIFLY&apos;s. Red means a term rests on something unknown.</p>
      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {data === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {accounts.map((a) => <AccountCard key={a.account.id} a={a} accounts={trustAccounts} onStated={refresh} />)}
      </div>
      <NoClientMoney rows={data?.no_client_money || []} />
      <Unattributed rows={data?.unattributed?.transactions || []} accounts={trustAccounts} onStated={refresh} />

      <BankExports onLoaded={refresh} />
    </div>
  );
}
